import React, { useEffect, useRef, useState } from 'react';
import { Form, InputGroup, Button, Spinner, Modal } from 'react-bootstrap';
import { FiX, FiPlus } from '../../utils/icons';

export interface SearchOption {
  key: string;   // valor armazenado
  label: string; // texto exibido
  extra?: string; // linha secundária opcional (ex.: CNPJ)
}

interface Props {
  placeholder?: string;
  disabled?: boolean;
  options: SearchOption[];
  value: string;                                   // key selecionada (ou texto livre)
  onSelect: (opt: SearchOption | null) => void;
  onAdd?: (nome: string, extra?: string) => Promise<SearchOption | null>; // criação inline (botão +)
  allowFreeText?: boolean;                          // texto digitado vira valor
  isInvalid?: boolean;
  // Campo extra opcional na janela de cadastro inline (ex.: E-mail, ao criar uma Pessoa
  // pelo botão + de Gestor Imediato) — a maioria dos usos (Tipo, Departamento, Diretoria)
  // não precisa disso e continua com o modal de campo único.
  extraField?: { label: string; type?: string; required?: boolean };
}

// Campo de pesquisa com autocomplete e criação inline ("+") — usado nos campos do
// Cadastro de Pessoas que vêm de outros cadastros (Tipo, Departamento, Diretoria...).
const SearchSelectField: React.FC<Props> = ({
  placeholder, disabled, options, value, onSelect, onAdd, allowFreeText, isInvalid, extraField,
}) => {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalNome, setModalNome] = useState('');
  const [modalExtra, setModalExtra] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const focusedRef = useRef(false);

  // Mantém o texto exibido sincronizado com o valor selecionado. Ignorado enquanto o
  // campo está focado, para não sobrescrever o que o usuário está digitando quando
  // `options` chega de forma assíncrona (ex.: cadastro auxiliar ainda carregando).
  useEffect(() => {
    if (focusedRef.current) return;
    const opt = options.find(o => o.key === value);
    setText(opt ? opt.label : (allowFreeText ? value : ''));
  }, [value, options, allowFreeText]);

  useEffect(() => {
    // Fase de CAPTURA (3º argumento `true`): precisa rodar antes da fase de bubble,
    // porque um clique em "Limpar"/numa opção da lista dispara um re-render síncrono
    // (React 18, evento discreto) que pode REMOVER o próprio elemento clicado do DOM
    // (ex.: o botão "Limpar" some quando o valor é limpo). Se o listener rodasse na
    // fase de bubble (como antes), `e.target` já estaria desanexado quando checássemos
    // `ref.current.contains(e.target)`, e essa checagem sempre retornaria falso — fechando
    // a lista logo depois de ela ter acabado de abrir.
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, []);

  const filtered = text.trim().length >= 1
    ? options.filter(o =>
        o.label.toLowerCase().includes(text.toLowerCase()) ||
        (o.extra || '').toLowerCase().includes(text.toLowerCase()))
    : options.slice(0, 30);

  const exactMatch = options.find(o => o.label.toLowerCase() === text.trim().toLowerCase());

  const handleBlurFreeText = () => {
    if (!allowFreeText) return;
    const t = text.trim();
    if (exactMatch) { onSelect(exactMatch); return; }
    onSelect(t ? { key: t, label: t } : null);
  };

  // Abre a janela de cadastro do novo item (botão "+"), pré-preenchendo com o que
  // já foi digitado no campo de busca, se houver.
  const openAddModal = () => {
    setModalNome(text.trim());
    setModalExtra('');
    setShowAddModal(true);
    setOpen(false);
  };

  const handleModalSave = async () => {
    if (!onAdd) return;
    const nome = modalNome.trim();
    if (!nome) return;
    if (extraField?.required && !modalExtra.trim()) return;
    const match = options.find(o => o.label.toLowerCase() === nome.toLowerCase());
    if (match) { onSelect(match); setText(match.label); setShowAddModal(false); setOpen(false); return; }
    setAdding(true);
    try {
      const novo = await onAdd(nome, modalExtra.trim() || undefined);
      if (novo) { onSelect(novo); setText(novo.label); setShowAddModal(false); setOpen(false); }
    } finally {
      setAdding(false);
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <InputGroup>
        <Form.Control
          value={text}
          disabled={disabled}
          placeholder={placeholder}
          isInvalid={isInvalid}
          onChange={e => { setText(e.target.value); setOpen(true); }}
          onFocus={() => { focusedRef.current = true; setOpen(true); }}
          onBlur={() => { focusedRef.current = false; handleBlurFreeText(); }}
        />
        {value && !disabled && (
          <Button
            type="button"
            variant="outline-secondary"
            // onMouseDown + preventDefault: age antes do blur do input roubar o clique.
            // setOpen(true): reabre a lista de opções imediatamente após limpar — sem
            // isso, o campo ficava vazio mas fechado até o usuário clicar fora e voltar.
            onMouseDown={e => { e.preventDefault(); onSelect(null); setText(''); setOpen(true); }}
            tabIndex={-1}
            title="Limpar"
          ><FiX /></Button>
        )}
        {onAdd && !disabled && (
          <Button
            type="button"
            variant="outline-primary"
            // onMouseDown + preventDefault: evita que o onBlur do input dispare antes e
            // feche a janela de cadastro que está prestes a abrir.
            onMouseDown={e => { e.preventDefault(); openAddModal(); }}
            title="Cadastrar novo"
          >
            <FiPlus />
          </Button>
        )}
      </InputGroup>
      {open && !disabled && filtered.length > 0 && (
        <div
          style={{
            position: 'absolute', zIndex: 1050, width: '100%', maxHeight: 240, overflowY: 'auto',
            background: '#fff', border: '1px solid #ced4da', borderTop: 'none',
            borderRadius: '0 0 .375rem .375rem', boxShadow: '0 .5rem 1rem rgba(0,0,0,.15)',
          }}
        >
          {filtered.map(o => (
            <button
              type="button"
              key={o.key}
              className="dropdown-item text-wrap"
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '.4rem .75rem', border: 'none', background: 'none' }}
              onMouseDown={e => { e.preventDefault(); onSelect(o); setText(o.label); setOpen(false); }}
            >
              {o.label}
              {o.extra && <span className="text-muted small ms-2">{o.extra}</span>}
            </button>
          ))}
        </div>
      )}

      {onAdd && (
        <Modal show={showAddModal} onHide={() => !adding && setShowAddModal(false)} centered>
          <Modal.Header closeButton><Modal.Title className="fs-6">Cadastrar novo</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group controlId="searchSelectAddNome">
              <Form.Label>Nome <span className="text-danger">*</span></Form.Label>
              <Form.Control
                value={modalNome}
                onChange={e => setModalNome(e.target.value)}
                autoFocus
                disabled={adding}
                onKeyDown={e => { if (e.key === 'Enter' && !extraField) { e.preventDefault(); handleModalSave(); } }}
              />
            </Form.Group>
            {extraField && (
              <Form.Group controlId="searchSelectAddExtra" className="mt-3">
                <Form.Label>{extraField.label} {extraField.required && <span className="text-danger">*</span>}</Form.Label>
                <Form.Control
                  type={extraField.type || 'text'}
                  value={modalExtra}
                  onChange={e => setModalExtra(e.target.value)}
                  disabled={adding}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleModalSave(); } }}
                />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={adding}>Cancelar</Button>
            <Button
              variant="primary"
              onClick={handleModalSave}
              disabled={adding || !modalNome.trim() || !!(extraField?.required && !modalExtra.trim())}
            >
              {adding ? <Spinner as="span" size="sm" animation="border" /> : 'Salvar'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default SearchSelectField;
